import * as React from 'react';

import { useEnterpriseSelfServePrice } from '@coursera-web/wes-common';
import type { ProductPrice } from '@coursera-web/wes-common';
import type { LocationDescriptorObject } from 'history';
import { compose, withProps } from 'recompose';

import type { NavigateFunction } from 'js/lib/useRouter';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import useUrlSessionStore from 'js/lib/useUrlSessionStore';
import user from 'js/lib/user';

import type { CheckoutSessionType } from 'bundles/teams-v2/components/TeamsMainApp';
import { getQueryFromLocation, getValidatedLearnerCountFromLocation } from 'bundles/teams-v2/components/utils';
import { NotificationProvider } from 'bundles/teams-v2/context/NotificationContext';
import useIsDifferentialPricingEnabled from 'bundles/teams-v2/hooks/useIsDifferentialPricingEnabled';
import { ErrorType, logToSentry } from 'bundles/teams-v2/utils/sentry';
import type {
  PropsFromPromotionEligibility,
  WithPromoCodeToPromotionProps,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';
import {
  withPromoCodeToPromotion,
  withPromotionEligibility,
  withRouterPromoCode,
} from 'bundles/teams-v2/utils/withEnterprisePromotion';

type PropsFromRouter = {
  location: LocationDescriptorObject;
  navigate: NavigateFunction;
  role?: string;
  organizationName?: string;
  organizationType?: string;
  organizationSizeCategory?: string;
  billingTypeStr?: string;
};

type MappedProps = {
  learnerCount?: number;
  billingType?: number;
};

export type Props = PropsFromRouter & MappedProps & PropsFromPromotionEligibility & WithPromoCodeToPromotionProps;

export type TeamsContextState = {} & WithPromoCodeToPromotionProps &
  PropsFromPromotionEligibility &
  MappedProps & {
    role?: string;
    organizationName?: string;
    organizationType?: string;
    organizationSizeCategory?: string;
    businessEmail?: string;
    fullName?: string;
    defaultProductPrice: ProductPrice | null;
    productPrice: ProductPrice | null;
  };

export const TeamsContext = React.createContext<TeamsContextState>({
  defaultProductPrice: null,
  productPrice: null,
});

export const TeamsContainer = (props: React.PropsWithChildren<Props>) => {
  const {
    learnerCount,
    billingType,
    isPromotionEligible,
    promoCode,
    promotion,
    enterprisePromotion,
    children,
    role,
    organizationName,
    organizationType,
    organizationSizeCategory,
  } = props;

  const enterprisePromotionId = isPromotionEligible ? enterprisePromotion?.id : undefined;
  const isDifferentialPricingEnabled = useIsDifferentialPricingEnabled(true);

  // Condition to decide which data source to use
  // Use the custom hook for clean data fetching
  const { productPrice, defaultProductPrice, error } = useEnterpriseSelfServePrice({
    learnerCount,
    billingType,
    enterprisePromotionId,
    isDifferentialPricingEnabled,
  });

  if (error) {
    logToSentry(error, ErrorType.GraphQLError, {
      message: 'Error fetching product price in TeamsContainer',
    });
    throw error;
  }

  const { sessionStore } = useUrlSessionStore<CheckoutSessionType>();

  const getBusinessEmail = () => {
    // TODO: Use hooks solution for sessionStore state
    if (typeof window === 'undefined') {
      return '';
    }

    let email = sessionStore.getState().businessEmail;
    if (!email) {
      if (user.isAuthenticatedUser()) {
        email = user.get().email_address;
      } else {
        email = '';
      }
    }

    return email;
  };
  const businessEmail = getBusinessEmail();

  const getFullName = () => {
    // TODO: Use hooks solution for sessionStore state
    // useUrlSessionStore causes a 500 on SSR without
    if (typeof window === 'undefined') {
      return '';
    }

    let name = sessionStore.getState().fullName;
    if (!name) {
      if (user.isAuthenticatedUser()) {
        name = user.get().full_name;
      } else {
        name = '';
      }
    }

    return name;
  };

  const fullName = getFullName();

  return (
    <NotificationProvider>
      <TeamsContext.Provider
        value={{
          promotion,
          enterprisePromotion,
          isPromotionEligible,
          promoCode,
          defaultProductPrice,
          productPrice,
          learnerCount,
          billingType,
          role,
          organizationName,
          organizationType,
          organizationSizeCategory,
          businessEmail,
          fullName,
        }}
      >
        {children}
      </TeamsContext.Provider>
    </NotificationProvider>
  );
};

const TeamsContainerHOC = compose<Props, PropsFromRouter>(
  // Conditionally find promotion information if not DD
  compose(withRouterPromoCode, withPromoCodeToPromotion, withPromotionEligibility),

  withProps<MappedProps, PropsFromRouter>(({ location, billingTypeStr }) => {
    const validatedLearnerCount = getValidatedLearnerCountFromLocation(location);

    return {
      learnerCount: validatedLearnerCount ?? undefined,
      billingType: billingTypeStr ? parseInt(billingTypeStr, 10) : undefined,
    };
  })
)(TeamsContainer);

const TeamsContainerWithRouter: React.LegacyFunctionComponentWithChildren<{}> = (props: {}) => {
  // Discretionary Discounts disable promotions
  const { sessionStore } = useUrlSessionStore<CheckoutSessionType>();
  const location = useLocation();
  const navigate = useNavigate();

  const getDataFromSessionStore = (key: keyof CheckoutSessionType) => {
    // TODO: Use hooks solution for sessionStore state
    if (typeof window === 'undefined') return '';
    return sessionStore.getState()[key];
  };
  // get all query params and session store params
  const role = getQueryFromLocation(location, 'role') || getDataFromSessionStore('role');
  const organizationName =
    getQueryFromLocation(location, 'organizationName') || getDataFromSessionStore('organizationName');
  const organizationType =
    getQueryFromLocation(location, 'organizationType') || getDataFromSessionStore('organizationType');
  const organizationSizeCategory =
    getQueryFromLocation(location, 'organizationSizeCategory') || getDataFromSessionStore('organizationSizeCategory');
  const billingTypeStr = getQueryFromLocation(location, 'billingType') ?? '1';

  return (
    <TeamsContainerHOC
      {...props}
      location={location}
      navigate={navigate}
      role={role}
      organizationName={organizationName}
      organizationType={organizationType}
      organizationSizeCategory={organizationSizeCategory}
      billingTypeStr={billingTypeStr}
    />
  );
};

export default TeamsContainerWithRouter;
